import { useEffect, useRef, useState, type FormEvent } from 'react'
import { AlertCircle, Check, Copy, Printer, QrCode, ShieldCheck } from 'lucide-react'
import { AuthShell } from './AuthShell.js'
import { consumeReturnTo } from '../shell/auth-routing.js'
import { enrollMfa, confirmMfa } from './api.js'

/**
 * /login/mfa-setup — enrôlement TOTP en trois temps :
 *   1. QR généré LOCALEMENT par le serveur (aucun service externe) + secret
 *      manuel en repli.
 *   2. Premier code valide → le MFA devient actif, pas avant.
 *   3. Codes de secours affichés UNE SEULE FOIS — copie / impression /
 *      confirmation explicite avant de continuer.
 */
export function MfaSetupPage() {
  const [step, setStep] = useState<'qr' | 'codes'>('qr')
  const [qr, setQr] = useState<string | null>(null)
  const [secret, setSecret] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [codes, setCodes] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [saved, setSaved] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    enrollMfa()
      .then((r) => { setQr(r.qr_data_url); setSecret(r.secret) })
      .catch(() => setError('Impossible de démarrer l’activation. Reconnectez-vous.'))
  }, [])

  async function confirm(e: FormEvent) {
    e.preventDefault()
    if (busy || code.length !== 6) return
    setBusy(true)
    setError(null)
    try {
      const r = await confirmMfa(code)
      if (r.ok) { setCodes(r.recovery_codes); setStep('codes'); return }
      setError('Code incorrect. Scannez le QR puis saisissez le code affiché.')
      setCode('')
      inputRef.current?.focus()
    } catch {
      setError('Le serveur est injoignable. Réessayez dans un instant.')
    } finally {
      setBusy(false)
    }
  }

  async function copyAll() {
    await navigator.clipboard.writeText(codes.join('\n'))
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (step === 'codes') {
    return (
      <AuthShell
        title="Codes de secours"
        subtitle="À conserver précieusement — ils ne seront plus jamais affichés"
        fondDiscret
      >
        <div className="lc-form">
          <p className="lc-form__help">
            Si vous perdez votre téléphone, un de ces codes vous permettra de vous
            connecter. Chaque code ne fonctionne qu’<strong>une seule fois</strong>.
          </p>
          <div className="lc-codes" role="list" aria-label="Codes de secours">
            {codes.map((c) => <span key={c} className="lc-code" role="listitem">{c}</span>)}
          </div>
          <div className="lc-codes__actions">
            <button type="button" className="lc-btn lc-btn--ghost" onClick={copyAll}>
              {copied ? <Check size={15} aria-hidden="true" /> : <Copy size={15} aria-hidden="true" />}
              {copied ? 'Copié' : 'Copier'}
            </button>
            <button type="button" className="lc-btn lc-btn--ghost" onClick={() => window.print()}>
              <Printer size={15} aria-hidden="true" />
              Imprimer
            </button>
          </div>
          <label className="lc-form__help" style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
            J’ai mis ces codes en lieu sûr.
          </label>
          <button
            type="button"
            className="lc-auth__cta"
            disabled={!saved}
            onClick={() => window.location.assign(consumeReturnTo('/'))}
          >
            Accéder au portail
          </button>
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell title="Activer la double vérification" subtitle="Votre compte l’exige avant la première connexion">
      <form className="lc-form" onSubmit={confirm} noValidate>
        {error ? (
          <p className="lc-form__error" role="alert">
            <AlertCircle size={16} aria-hidden="true" />
            {error}
          </p>
        ) : null}

        <div className="lc-field" style={{ alignItems: 'center' }}>
          {qr ? (
            <img
              src={qr}
              alt="QR code à scanner avec une application d’authentification"
              width={200}
              height={200}
              style={{ borderRadius: 14, border: '1px solid var(--border)' }}
            />
          ) : (
            <div style={{ width: 200, height: 200, display: 'grid', placeItems: 'center', color: 'var(--text-3)' }}>
              <QrCode size={40} aria-hidden="true" />
            </div>
          )}
          {secret ? (
            <p className="lc-form__help" style={{ textAlign: 'center' }}>
              Sans caméra : saisissez ce code dans l’application —{' '}
              <code style={{ fontFamily: 'var(--font-mono)', userSelect: 'all' }}>{secret}</code>
            </p>
          ) : null}
        </div>

        <div className="lc-field">
          <label className="lc-label" htmlFor="setup-code">Code affiché par l’application</label>
          <input
            ref={inputRef}
            id="setup-code"
            className="lc-otp"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="······"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            disabled={busy}
            required
          />
        </div>

        <button className="lc-auth__cta" type="submit" disabled={busy || code.length !== 6}>
          {busy ? <span className="lc-spin" aria-hidden="true" /> : <ShieldCheck size={17} aria-hidden="true" />}
          {busy ? 'Activation…' : 'Activer'}
        </button>
      </form>
    </AuthShell>
  )
}
